/**
 * Sổ đăng ký giao diện sự kiện.
 *
 * Mỗi dịp lễ là MỘT mục ở đây. Hình học, hiệu ứng, mask, nhánh giảm-chuyển-động —
 * tất cả nằm chung trong `styles.css` và `components/event/`, không nhân bản theo lễ.
 *
 * 🔴 **Thêm một lễ = ba chỗ, không hơn:**
 *   1. `EVENT_THEMES` ở dưới (mục này),
 *   2. một khối `[data-event="<slug>"]` bốn-sáu dòng màu trong `styles.css`,
 *   3. hai file asset trong `public/events/<dir>/`.
 * Phải sửa chỗ thứ tư nghĩa là có quy tắc nào đó đang viết cứng thứ của riêng 2/9 —
 * sửa quy tắc ấy, đừng thêm nhánh mới.
 *
 * ⚠️ `slug` phải TRÙNG giá trị `ui_mode` trong bảng `ui_config` phía backend
 * (migration v101). Lệch một ký tự thì máy chủ bật lễ mà FE không tìm thấy chủ đề
 * nào để dựng — và nó rơi về giao diện thường trong im lặng.
 */

export const EVENT_SLUGS = [
  "quoc_khanh",
  "trung_thu",
  "phu_nu_viet_nam",
  "halloween",
  "noel",
  "tet",
  "thong_nhat",
  "di_san",
] as const;

export type EventSlug = (typeof EVENT_SLUGS)[number];

/** Chế độ giao diện đang bật: `normal` hoặc một slug sự kiện. */
export type UiMode = "normal" | EventSlug;

/**
 * Kiểu chuyển động của hình trên thẻ. Bốn lớp dùng chung trong `styles.css`
 * (`.event-motif--<motion>`) — thêm một lớp mới thì phải thêm cả vào nhánh
 * `prefers-reduced-motion`, nếu không trợ năng hỏng trong im lặng.
 *
 * - `sway`   — treo đầu trên, lắc nhẹ như đèn lồng / quả châu / bao lì xì
 * - `bob`    — bồng bềnh lên xuống, cho vật không có dây treo
 * - `flutter` — phập phồng như vải, dùng lại nhịp của lá cờ 2/9
 * - `spin`   — quay tròn rất chậm, cho vật hình đĩa (trống đồng)
 */
export type MotifMotion = "sway" | "bob" | "flutter" | "spin";

export type EventMotif = {
  /**
   * Ảnh PNG/WEBP **có kênh trong suốt**, tỉ lệ 1.6 (vd 640×400).
   *
   * ⚠️ Ảnh KHÔNG được nướng sẵn bóng đổ: vệt sáng lụa chạy trên hình lấy chính
   * kênh alpha của ảnh làm mask (`mask-image`), nên bóng mờ quanh vật sẽ biến
   * thành một vùng loang sáng hình chữ nhật mờ.
   */
  image: string;
  motion: MotifMotion;
  /** Tắt vệt sáng lụa cho hình không phải chất liệu bóng (vd trống đồng xỉn). */
  shine?: boolean;
};

/**
 * Loại hạt bay trên nền trang chủ.
 *
 * Bốn loại phủ đủ bảy lễ: khác nhau ở HÌNH và ánh sáng, không ở chuyển động —
 * chuyển động do `drift` quyết định. Thêm loại mới thì thêm một lớp
 * `.event-particle--<kind>` trong `styles.css` (chỉ hình + màu, không animation).
 */
export type ParticleKind =
  /** Cánh hoa: hình lá bầu, xoay khi bay. Hoa mai Tết, cánh hoa 2/9. */
  | "petal"
  /** Bông tuyết: đốm tròn trắng có viền sáng lạnh. */
  | "snow"
  /** Ngôi sao bốn cánh, lấp lánh. */
  | "star"
  /** Đốm sáng nhỏ có quầng — tàn lửa, bụi vàng, đom đóm. */
  | "spark";

export type EventAmbient = {
  kind: ParticleKind;
  /**
   * Hướng bay. `rise` = dâng lên tại chỗ khoảng 110px rồi tan (nhịp gốc của 2/9);
   * `fall` = rơi xuyên cả màn hình, có lắc ngang.
   *
   * ⚠️ `fall` chậm hơn `rise` nhiều (9–17s so với 5,8–8,1s) vì nó đi trọn 110vh
   * chứ không phải 110px. Cho `fall` một khoảng thời gian ngắn thì hạt lao xuống
   * như mưa đá.
   */
  drift: "rise" | "fall";
  /** Số hạt. Mặc định 14. */
  count?: number;
  /**
   * Số vệt sao băng vụt qua vùng trời phía trên. 0 / bỏ trống = không có.
   *
   * ⚠️ Chúng CỐ Ý thưa: mỗi vệt chỉ hiện trong ~1/5 chu kỳ 15–22 giây. Sao băng
   * mà vụt liên tục thì hết là sao băng.
   */
  shootingStars?: number;
  /**
   * Số bản THU NHỎ của chính `motif` (đèn ông sao, bí ngô, quả châu…) trôi lẫn trong
   * đám hạt. 0 / bỏ trống = không có.
   *
   * 🔴 Đây là lớp THỨ HAI, không thay lớp hạt. Cho cả trường hạt thành hai mươi quả
   * châu thì mất hẳn cảm giác tuyết/sao — hình có chi tiết mà xếp dày thì đọc ra là
   * hoa văn dán, không phải thứ đang bay. Vài cái lẫn trong đám hạt trừu tượng mới
   * ra chiều sâu.
   *
   * ⚠️ Chỉ có tác dụng khi lễ đã khai `motif`; thiếu ảnh thì lớp này tự bỏ.
   */
  motifCount?: number;
  /** Ba quầng mờ thở ở chân trang (bố cục gốc của 2/9). */
  glow?: boolean;
};

export type EventTheme = {
  slug: EventSlug;
  /** Tên hiển thị — dùng cho `aria-label` và nhật ký, không hiện thẳng ra giao diện. */
  label: string;
  /** Cùng bảng màu với giao diện Event trên app. */
  palette: {
    primary: string;
    gold: string;
    goldLight: string;
    soft: string;
    hero: string;
  };
  /**
   * Asset dưới `public/events/`.
   *
   * ⚠️ Chỉ `background` là BẮT BUỘC. Cặp `navbarVideo`/`navbarPoster` là TUỲ CHỌN:
   * lễ chưa làm banner thì bỏ trống, và thanh điều hướng giữ nguyên dáng thường —
   * đó là suy giảm đúng đắn. Khai một nửa (có video, thiếu poster) thì coi như không
   * có, vì thẻ `<video>` cần poster để không loé khung trống lúc chờ tải.
   */
  assets: {
    /** Banner trang chủ, dùng thay cho nền Event toàn màn. */
    homeBanner: string;
    /** Ảnh bìa toàn màn của trang chủ. BẮT BUỘC. */
    background: string;
    /** Clip banner thanh điều hướng (mobile). Bỏ trống = không có banner. */
    navbarVideo?: string;
    /** Khung tĩnh của clip: vừa là `poster`, vừa là bản thay khi giảm chuyển động. */
    navbarPoster?: string;
  };
  /**
   * Hiệu ứng hạt bay trên nền trang chủ. TUỲ CHỌN — bỏ trống thì chỉ còn ảnh bìa
   * tĩnh, và đó là suy giảm đúng đắn cho lễ chưa chọn được kiểu hạt.
   */
  ambient?: EventAmbient;
  /**
   * Hình động bên phải thẻ lời chúc. TUỲ CHỌN — lễ chưa có hình thì thẻ bỏ hẳn ô
   * và chữ trải hết chiều ngang (để ô rỗng là chừa một khoảng trống vô cớ).
   *
   * ⚠️ 2/9 KHÔNG khai trường này: lá cờ của nó là SVG vẽ tay (`QuocKhanhFlag`),
   * nét vector nên sắc ở mọi mật độ điểm và không tốn thêm một lượt tải nào.
   * Trường này là đường cho hình RASTER có kênh trong suốt — xem `EventMotif`.
   */
  motif?: EventMotif;
  /** Thẻ lời chúc trên trang chủ. */
  card: {
    /**
     * Emoji đứng trước tiêu đề. TUỲ CHỌN — bỏ trống thì tiêu đề bắt đầu ngay từ mép,
     * không chừa khoảng trống.
     *
     * ⚠️ Emoji do FONT HỆ THỐNG vẽ, không phải mình kiểm soát. `🥁` từng dùng cho
     * Ngày Di sản và nó ra một cái trống DÀN NHẠC phương Tây màu đỏ — lệch hẳn tông
     * đồng–giấy dó của lễ. Không có emoji trống đồng, nên lễ này bỏ hẳn emoji.
     */
    emoji?: string;
    /** Nhãn dán ngắn, viết hoa. */
    title: string;
    /** Chữ trên đường dẫn hành động. */
    cta: string;
    /** Đường dẫn nội bộ mà CTA trỏ tới. */
    to: string;
  };
};

export const EVENT_THEMES: Record<EventSlug, EventTheme> = {
  quoc_khanh: {
    slug: "quoc_khanh",
    label: "Quốc khánh 2/9",
    palette: {
      primary: "#d71920",
      gold: "#b87900",
      goldLight: "#fad02c",
      soft: "#fff1ef",
      hero: "#8e1015",
    },
    assets: {
      homeBanner: "/events/quoc-khanh/home-banner.webp",
      background: "/events/2-9/national-day-bg.webp",
      navbarVideo: "/events/2-9/navbar-quockhanh.mp4",
      navbarPoster: "/events/2-9/navbar-quockhanh.webp",
    },
    // Hiệu ứng nền: Giữ đúng nhịp cũ: sáu cánh hoa dâng lên. ⚠️ Vị trí từng cánh nay do
    // hàm ngẫu-nhiên-tất-định sinh nên KHÁC bản đặt tay trước đây; kích cỡ,
    // màu, thời lượng và độ trễ thì vẫn nằm trong đúng khoảng cũ.
    ambient: {
      kind: "petal",
      drift: "rise",
      count: 6,
      glow: true,
    },
    card: {
      emoji: "🇻🇳",
      title: "MỪNG QUỐC KHÁNH 2/9",
      cta: "Gieo quẻ cầu an & thịnh vượng",
      to: "/kinh-dich",
    },
  },

  trung_thu: {
    slug: "trung_thu",
    label: "Trung Thu",
    palette: {
      primary: "#d2492a",
      gold: "#a8701c",
      goldLight: "#efb63c",
      soft: "#eaddc6",
      hero: "#7e2a12",
    },
    assets: {
      homeBanner: "/events/trung-thu/home-banner.webp",
      background: "/events/trung-thu/bg.webp",
      navbarVideo: "/events/trung-thu/navbar.mp4",
      navbarPoster: "/events/trung-thu/navbar.webp",
    },
    // Hình bên phải thẻ: đèn ông sao treo.
    motif: {
      image: "/events/trung-thu/motif.webp",
      motion: "sway",
    },
    // Hiệu ứng nền: Sao vàng rơi chậm giữa trời rằm, thêm hai vệt sao băng.
    ambient: {
      kind: "star",
      drift: "fall",
      count: 16,
      shootingStars: 2,
      // 5 bản thu nhỏ của đèn ông sao trôi lẫn trong đám hạt
      motifCount: 5,
      glow: true,
    },
    card: {
      emoji: "🏮",
      title: "TẾT ĐOÀN VIÊN",
      cta: "Khám phá vận trình mùa trăng",
      to: "/kinh-dich",
    },
  },
  phu_nu_viet_nam: {
    slug: "phu_nu_viet_nam",
    label: "Ngày Phụ nữ Việt Nam 20/10",
    // Cùng bảng màu với `boide-mobile/src/lib/events/registry.ts`.
    palette: {
      primary: "#b65373",
      gold: "#9d5b64",
      goldLight: "#e8a3b1",
      soft: "#f2daba",
      hero: "#79394e",
    },
    // Asset lấy nguyên từ bản mobile (`home-banner-v3.webp` → `home-banner.webp`).
    //
    // ⚠️ Lễ này KHÔNG có ảnh bìa toàn màn riêng: `background` trỏ về chính banner
    // trang chủ. `EventBackground` hiện không được gắn ở route nào (Home v2 chỉ dùng
    // banner), nên đây chỉ là giá trị lấp trường bắt buộc — làm ảnh bìa thật thì
    // thêm `bg.webp` rồi đổi dòng này.
    assets: {
      homeBanner: "/events/phu-nu-viet-nam/home-banner.webp",
      background: "/events/phu-nu-viet-nam/home-banner.webp",
      navbarVideo: "/events/phu-nu-viet-nam/navbar.mp4",
      navbarPoster: "/events/phu-nu-viet-nam/navbar.webp",
    },
    // Không có `motif`: giống bản mobile, thẻ lời chúc để chữ trải hết chiều ngang.
    // Hiệu ứng nền: cánh hoa dâng nhẹ, khớp `petal` của mobile.
    ambient: {
      kind: "petal",
      drift: "rise",
      count: 8,
      glow: true,
    },
    card: {
      // Không có emoji, giống bản mobile.
      title: "MỪNG NGÀY 20/10",
      cta: "Gieo quẻ gửi lời chúc an lành",
      to: "/kinh-dich",
    },
  },
  halloween: {
    slug: "halloween",
    label: "Halloween",
    palette: {
      primary: "#de6a18",
      gold: "#6e4e96",
      goldLight: "#c2a0e4",
      soft: "#e2cae2",
      hero: "#46255f",
    },
    assets: {
      homeBanner: "/events/halloween/home-banner.webp",
      background: "/events/halloween/bg.webp",
      navbarVideo: "/events/halloween/navbar.mp4",
      navbarPoster: "/events/halloween/navbar.webp",
    },
    // Hình bên phải thẻ: bí ngô khắc mặt.
    motif: {
      image: "/events/halloween/motif.webp",
      motion: "bob",
    },
    // Hiệu ứng nền: Tàn lửa tím DÂNG LÊN, không rơi: đốm sáng bay lên đọc ra là hồn/khói,
    // rơi xuống thì thành mưa — mà ảnh bìa đã có sẵn khói tím cuộn lên.
    ambient: {
      kind: "spark",
      drift: "rise",
      count: 18,
      // 4 bản thu nhỏ của bí ngô trôi lẫn trong đám hạt
      motifCount: 4,
      glow: true,
    },
    card: {
      emoji: "🎃",
      title: "ĐÊM HALLOWEEN",
      cta: "Gieo quẻ trong đêm huyền bí",
      to: "/kinh-dich",
    },
  },
  noel: {
    slug: "noel",
    label: "Giáng sinh",
    palette: {
      primary: "#be3327",
      gold: "#9c7a2e",
      goldLight: "#e8b54a",
      soft: "#e0dfe0",
      hero: "#1d5537",
    },
    assets: {
      homeBanner: "/events/noel/home-banner.webp",
      background: "/events/noel/bg.webp",
      navbarVideo: "/events/noel/navbar.mp4",
      navbarPoster: "/events/noel/navbar.webp",
    },
    // Hình bên phải thẻ: quả châu Noel treo.
    motif: {
      image: "/events/noel/motif.webp",
      motion: "sway",
    },
    // Hiệu ứng nền: Tuyết rơi — hiệu ứng biểu tượng của lễ này, nhiều hạt nhất trong bảy lễ.
    ambient: {
      kind: "snow",
      drift: "fall",
      count: 22,
      // 5 bản thu nhỏ của quả châu trôi lẫn trong đám hạt
      motifCount: 5,
      glow: true,
    },
    card: {
      emoji: "🎄",
      title: "MỪNG GIÁNG SINH",
      cta: "Gieo quẻ đón mùa an lành",
      to: "/kinh-dich",
    },
  },
  tet: {
    slug: "tet",
    label: "Tết Nguyên Đán",
    palette: {
      primary: "#c8102e",
      gold: "#a0701a",
      goldLight: "#f0b429",
      soft: "#fae3ce",
      hero: "#8e1220",
    },
    assets: {
      homeBanner: "/events/tet/home-banner.webp",
      background: "/events/tet/bg.webp",
      navbarVideo: "/events/tet/navbar.mp4",
      navbarPoster: "/events/tet/navbar.webp",
    },
    // Hình bên phải thẻ: bao lì xì đỏ treo cành mai.
    motif: {
      image: "/events/tet/motif.webp",
      motion: "sway",
    },
    // Hiệu ứng nền: Cánh hoa mai vàng rơi.
    ambient: {
      kind: "petal",
      drift: "fall",
      count: 16,
      // 5 bản thu nhỏ của bao lì xì trôi lẫn trong đám hạt
      motifCount: 5,
      glow: true,
    },
    card: {
      emoji: "🧧",
      title: "MỪNG XUÂN MỚI",
      cta: "Xin quẻ đầu năm",
      to: "/kinh-dich",
    },
  },
  thong_nhat: {
    slug: "thong_nhat",
    label: "Thống nhất đất nước 30/4",
    palette: {
      primary: "#d9482a",
      gold: "#a8721c",
      goldLight: "#f5c63c",
      soft: "#fdf8dc",
      hero: "#9e3a1c",
    },
    assets: {
      homeBanner: "/events/thong-nhat/home-banner.webp",
      background: "/events/thong-nhat/bg.webp",
      navbarVideo: "/events/thong-nhat/navbar.mp4",
      navbarPoster: "/events/thong-nhat/navbar.webp",
    },
    // Hình bên phải thẻ: cờ Giải phóng.
    motif: {
      image: "/events/thong-nhat/motif.webp",
      motion: "flutter",
    },
    // Hiệu ứng nền: Cùng nhịp 2/9 — hai lễ cùng hệ hình ảnh.
    ambient: {
      kind: "petal",
      drift: "rise",
      count: 8,
      // 4 bản thu nhỏ của cờ Giải phóng trôi lẫn trong đám hạt
      motifCount: 4,
      glow: true,
    },
    card: {
      emoji: "🕊️",
      title: "MỪNG NGÀY THỐNG NHẤT",
      cta: "Gieo quẻ cầu an & thịnh vượng",
      to: "/kinh-dich",
    },
  },
  di_san: {
    slug: "di_san",
    label: "Ngày Di sản Văn hoá Việt Nam",
    palette: {
      primary: "#8a5a2a",
      gold: "#7a5520",
      goldLight: "#b8862e",
      soft: "#f2dfb9",
      hero: "#6b4720",
    },
    assets: {
      homeBanner: "/events/di-san/home-banner.webp",
      background: "/events/di-san/bg.webp",
      navbarVideo: "/events/di-san/navbar.mp4",
      navbarPoster: "/events/di-san/navbar.webp",
    },
    // Hình bên phải thẻ: bình gốm hoa lam.
    //
    // 🔴 ĐỔI TỪ mặt trống đồng Đông Sơn (04/09/2026, theo phản hồi chủ dự án). Mặt
    // trống là một cái ĐĨA phủ kín hoa văn mảnh; ở ô 56px nó mất sạch chi tiết và chỉ
    // còn lại một khối nâu tròn nằm trên nền kem — không đọc ra là cái gì. Bình gốm
    // thắng ở đúng hai điểm quyết định trong một ô nhỏ: DÁNG có cổ và thân phình nên
    // nhận ra qua silhouette, và men LAM là màu duy nhất trong cả sáu lễ tương phản
    // mạnh với nền kem.
    //
    // Kéo theo `motion` đổi từ `spin` sang `bob`: `spin` chỉ đúng cho vật đối xứng
    // tâm, cho một cái bình quay tròn thì nó thành đồ chơi.
    motif: {
      image: "/events/di-san/motif.webp",
      motion: "bob",
      // `shine` để mặc định (bật): men gốm bóng thì bắt sáng thật, khác đồng xỉn.
    },
    // Hiệu ứng nền: Bụi vàng dâng lên như bụi trong luồng sáng bảo tàng.
    ambient: {
      kind: "spark",
      drift: "rise",
      count: 12,
      glow: true,
    },
    card: {
      // Không có emoji: xem ghi chú ở khai báo `emoji`.
      title: "NGÀY DI SẢN VĂN HOÁ",
      cta: "Tìm lại tri thức của người xưa",
      to: "/kinh-dich",
    },
  },
};

export function isEventSlug(value: unknown): value is EventSlug {
  return typeof value === "string" && (EVENT_SLUGS as readonly string[]).includes(value);
}

export function isUiMode(value: unknown): value is UiMode {
  return value === "normal" || isEventSlug(value);
}

/** Chủ đề đang bật, hoặc `null` khi đang ở giao diện thường. */
export function themeOf(mode: UiMode): EventTheme | null {
  return mode === "normal" ? null : EVENT_THEMES[mode];
}

/**
 * Banner thanh điều hướng của lễ này, hoặc `null` khi lễ chưa có banner.
 *
 * Đòi ĐỦ CẢ HAI file: thẻ `<video>` không có `poster` sẽ hiện một khung trống trong
 * lúc tải, và bản tĩnh (máy yếu / giảm chuyển động) không có gì để vẽ.
 */
export type NavbarClip = { video: string; poster: string };

export function navbarClipOf(theme: EventTheme): NavbarClip | null {
  const { navbarVideo, navbarPoster } = theme.assets;
  return navbarVideo && navbarPoster ? { video: navbarVideo, poster: navbarPoster } : null;
}
